import { useMemo } from 'react';
import * as Astronomy from 'astronomy-engine';
import {
  ASTRO_BODIES,
  altAz,
  moonIllumination,
  moonPhaseName,
  nextRiseSet,
  renderNightOverlay,
  subPoint,
  type BodyState,
} from '@/lib/astro';
import { useNow } from '@/hooks/useNow';
import type { GeoPoint } from '@/lib/geo';

export interface AstronomyState {
  bodies: BodyState[];
  /** Data URL of the equirectangular night overlay (recomputed every minute). */
  nightUrl: string;
}

/** Sun/Moon/planet positions: zenith points worldwide + alt/az for the observer. */
export function useAstronomy(location: GeoPoint, nightStrength = 1): AstronomyState {
  const now = useNow(20_000);
  const minute = Math.floor(now.getTime() / 60_000);

  const bodies = useMemo<BodyState[]>(
    () =>
      ASTRO_BODIES.map((b) => {
        const state: BodyState = {
          ...b,
          sub: subPoint(b.body, now),
          ...altAz(b.body, now, location),
        };
        if (b.body === Astronomy.Body.Sun || b.body === Astronomy.Body.Moon) {
          const rs = nextRiseSet(b.body, now, location);
          state.rise = rs.rise;
          state.set = rs.set;
        }
        if (b.body === Astronomy.Body.Moon) {
          state.moonIllum = moonIllumination(now);
          state.moonPhaseName = moonPhaseName(now);
        }
        return state;
      }),
    [now, location],
  );

  const nightUrl = useMemo(
    () => renderNightOverlay(new Date(minute * 60_000), nightStrength),
    [minute, nightStrength],
  );

  return { bodies, nightUrl };
}
